import {
  useState,
  type FormEvent,
} from 'react';

import {
  signInWithEmailAndPassword,
} from 'firebase/auth';

import {
  useAuth,
} from '../auth';

import {
  auth,
  isFirebaseConfigured,
} from '../firebase';

import { Splash } from './ui';

function friendlyError(code: string): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';

    case 'auth/invalid-email':
      return 'That does not look like an email address.';

    case 'auth/user-disabled':
      return 'This account has been disabled.';

    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';

    case 'auth/network-request-failed':
      return 'Could not reach the authentication service.';

    default:
      return 'Sign-in failed. Please try again.';
  }
}

const submitButton =
  'mt-0.5 flex min-h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-[9px] border-0 bg-primary px-4 py-2.5 text-center text-white shadow-[0_3px_10px_rgba(14,26,43,0.18)] active:enabled:translate-y-px enabled:hover:bg-primary-strong enabled:hover:shadow-[0_5px_14px_rgba(14,26,43,0.22)] disabled:cursor-not-allowed disabled:opacity-55';

export default function Login() {
  const {
    status,
    reason,
    firebaseUser,
    signOut,
  } = useAuth();

  /*
   * Signed in with Firebase but refused by the API: say so and offer a way
   * out, rather than showing a sign-in form that will keep succeeding.
   */
  const blocked =
    status === 'unapproved' && firebaseUser;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] =
    useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    setError(null);

    if (!auth) {
      setError(
        'Authentication is not configured.',
      );
      return;
    }

    setBusy(true);

    try {
      await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
    } catch (err) {
      const code =
        typeof err === 'object' &&
        err !== null &&
        'code' in err
          ? String(
              (err as { code?: unknown })
                .code ?? '',
            )
          : '';

      if (code.startsWith('auth/')) {
        setError(friendlyError(code));
      } else {
        setError(
          'Sign-in failed. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  if (busy) {
    return <Splash message="Signing in..." />;
  }

  return (
    <main className="relative z-1 grid min-h-screen place-items-center px-5 py-8 dark:z-2 upto-520:px-3.5 upto-520:py-5">
      <div className="flex w-[min(100%,440px)] flex-col items-center">

        <div className="mb-6 flex items-center justify-center gap-2.5 upto-520:mb-[18px]">
          <span
            className="grid size-[38px] place-items-center text-primary"
            aria-hidden="true"
          >
            <svg
              className="align-baseline"
              viewBox="0 0 32 32"
              width="36"
              height="36"
            >
              <rect
                width="32"
                height="32"
                rx="9"
                fill="currentColor"
              />

              <path
                d="M7 20c2.4-7 4.6-9 6.6-9s2.2 7 4.2 7 3-4.5 6.2-4.5"
                stroke="var(--surface)"
                strokeWidth="2.4"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>

          <span className="text-[24px] font-extrabold tracking-[-0.04em] text-fg">
            nxt
            <span className="text-accent">Cloud</span>
          </span>
        </div>

        <section className="w-full rounded-[16px] border border-edge bg-surface p-7 dark:shadow-[0_30px_80px_rgba(0,0,0,0.5)] upto-520:rounded-[13px] upto-520:px-[18px] upto-520:py-[22px]">
          <div className="mb-[22px]">
            <div className="mb-1.5 text-[10px] font-extrabold tracking-[0.1em] text-primary uppercase">
              Secure access
            </div>

            <h1>
              {blocked ? 'Account not ready' : 'Sign in'}
            </h1>

            <p className="mt-[7px] text-fg-3">
              {blocked ? (
                <>
                  You are signed in as <strong>{firebaseUser.email}</strong>, but the server
                  has not granted access.
                </>
              ) : (
                'Access the nxtCloud gait analysis dashboard.'
              )}
            </p>
          </div>

          {!isFirebaseConfigured && (
            <div
              className="flex items-start rounded-[9px] border border-[#efcaca] bg-danger-soft px-3 py-[11px] text-[12px] leading-[1.45] text-danger dark:border-danger/40"
              role="alert"
            >
              This deployment has no Firebase
              configuration, so sign-in cannot work. Set the
              VITE_FIREBASE_* variables on the Pages project and redeploy.
            </div>
          )}

          {status === 'signed-out' &&
            reason && (
              <div
                className="flex items-start rounded-[9px] border border-[#efcaca] bg-danger-soft px-3 py-[11px] text-[12px] leading-[1.45] text-danger dark:border-danger/40"
                role="alert"
              >
                {reason}
              </div>
            )}

          {blocked ? (
            <>
              {reason && (
                <div
                  className="mb-4 flex items-start rounded-[9px] border border-warn/30 bg-warn-soft px-3 py-[11px] text-[12px] leading-[1.45] text-warn"
                  role="alert"
                >
                  {reason}
                </div>
              )}

              <button
                type="button"
                className={submitButton}
                onClick={() => void signOut()}
              >
                Sign out
              </button>
            </>
          ) : (
            <form
              className="flex flex-col gap-4"
              onSubmit={onSubmit}
            >
              <label className="flex flex-col gap-[7px]">
                <span className="font-[650] text-fg-2">
                  Email
                </span>

                <input
                  className="block min-h-11 w-full rounded-[9px] border px-3 py-2.5 [transition:border-color_140ms,box-shadow_140ms,background_140ms] placeholder:text-fg-3 focus:shadow-[0_0_0_3px_var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-65 dark:border-edge dark:bg-surface dark:text-fg dark:enabled:hover:border-edge-strong dark:focus:border-primary"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) =>
                    setEmail(event.target.value)
                  }
                  placeholder="you@example.com"
                  required
                  autoFocus
                  disabled={busy}
                />
              </label>

              <label className="flex flex-col gap-[7px]">
                <span className="font-[650] text-fg-2">
                  Password
                </span>

                <input
                  className="block min-h-11 w-full rounded-[9px] border px-3 py-2.5 [transition:border-color_140ms,box-shadow_140ms,background_140ms] placeholder:text-fg-3 focus:shadow-[0_0_0_3px_var(--primary-soft)] disabled:cursor-not-allowed disabled:opacity-65 dark:border-edge dark:bg-surface dark:text-fg dark:enabled:hover:border-edge-strong dark:focus:border-primary"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) =>
                    setPassword(event.target.value)
                  }
                  placeholder="Enter your password"
                  required
                  disabled={busy}
                />
              </label>

              {error && (
                <div
                  className="flex items-start rounded-[9px] border border-[#efcaca] bg-danger-soft px-3 py-[11px] text-[12px] leading-[1.45] text-danger dark:border-danger/40"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                className={submitButton}
                disabled={!isFirebaseConfigured}
              >
                Sign in
              </button>
            </form>
          )}

          <div className="mt-5 flex items-start gap-[9px] border-t border-edge pt-4">
            <span className="grid size-6 flex-[0_0_24px] place-items-center rounded-[7px] bg-ok-soft text-[12px] font-extrabold text-ok">
              ✓
            </span>

            <p className="mt-0.5 text-fg-3">
              Accounts are created by an
              administrator. There is no
              self-service sign-up.
            </p>
          </div>
        </section>

        <p className="mt-4 text-fg-3">
          nxtCloud · Gait Analysis Dashboard
        </p>
      </div>
    </main>
  );
}